import React, { useCallback, useEffect, useMemo, useRef } from "react"
import { useMountedState } from "react-use"

type StoreConfig = {
  store: string
  watch: boolean | string | Array<string>
}

type ConnectedStores = Record<string, any>

function useStores(stores: StoreConfig[]) {
  // A hook that will attach stores as well as triggering state updates
  // when watch happens.

  // Store state is passed as props to the wrapped component using the following pattern:
  // - @props.<store>Store.state: The current state of the store (plain JavaScript object)
  // - @props.<store>Store.action: A reference to available actions provided by the store.
  // - @props.<store>Store.request: A reference to available requests provided by the store.\

  const [counter, setCounter] = React.useState(0)
  const isMounted = useMountedState()

  const handleWatchEvent = useCallback(function handleWatchEvent(store: any) {
    // check for race condition
    if (!isMounted()) {
      return
    }

    if (!store) {
      console.error(
        "`useStores` received a state change event with the wrong scope! Check that the state change event was triggered properly."
      )
      console.trace()
    }

    setCounter((prevCounter) => prevCounter + 1)
  }, [])

  function connectStores() {
    const connectedStores = {} as ConnectedStores

    for (var store of Array.from(stores)) {
      connectedStores[store.store] = window.App.stores.requestStore(store.store)

      const parseStateChangeEvent = function (ev: string) {
        if (typeof ev !== "string") {
          const err = `Invalid watch key for store ${store}. Watch keys must be strings.`
          console.error(err)
          console.trace()
          throw new Error(err)
        }
        if (ev.indexOf("change:") === -1) {
          ev = `change:${ev}`
        }
        return ev
      }

      if (Array.isArray(store.watch)) {
        const events = store.watch.reduce((events, ev) => {
          ev = parseStateChangeEvent(ev)
          if (ev === "change") {
            console.error(
              `Invalid state change event for store ${store}. You cannot listen to 'change', you must specify a key!`
            )
            throw new Error(
              "invalid state change event... you must specify a key!"
            )
          }
          if (!connectedStores[store.store])
            throw new Error(`invalid store key ${store.store}`)

          return events ? `${events} ${ev}` : ev
        }, "")

        connectedStores[store.store].on(events, handleWatchEvent)
      }
    }

    return connectedStores
  }

  function disconnectStores() {
    Object.keys(connectedStoresRef.current).forEach((storeKey) => {
      connectedStoresRef.current[storeKey].off(null, handleWatchEvent)
    })
  }

  function getStoreState(store: string) {
    return {
      state: connectedStoresRef.current[store].getState(),
      action: connectedStoresRef.current[store].action,
      request: connectedStoresRef.current[store].request
    }
  }

  function getState() {
    const state = stores.reduce(function (state: any, store: ConnectedStores) {
      return {
        ...state,
        [`${store.store}Store`]: getStoreState(store.store)
      }
    }, {})

    return state
  }

  const connectedStoresRef = useRef(useMemo(connectStores, []))
  const state = useMemo(getState, [counter])

  useEffect(() => {
    return disconnectStores
  }, [])

  return state
}

export default useStores
